<template>
    <div>
        <b-form-row v-if="!formDisabled" class="justify-content-md-center">
            <label class="table-title">Uploaded Documents</label>
        </b-form-row>

        <b-form-row class="justify-content-md-center">
            <b-col lg="6" class="my-1">
                <b-form-group
                    label="Filter"
                    label-for="filter-input"
                    label-cols-sm="3"
                    label-align-sm="right"
                    label-size="sm"
                    class="mb-0"
                >
                    <b-input-group size="sm">
                        <b-form-input
                            id="filter-input"
                            v-model="filter"
                            type="search"
                            placeholder="Type to Search"
                        />

                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">
                                Clear
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-table
                bordered
                striped
                hover
                responsive
                :filter="filter"
                :fields="uploadedFilesTableFields"
                :items="uploadedFilesTableItems"
            >
                <template v-slot:head(actions)>
                    <b-form-row v-if="isApplicant" class="pr-2 text-right">
                        <b-col class="actions-column">
                            <fa-icon fixed-width
                                     size="lg"
                                     title="Download"
                                     :icon="['fas','download']"
                            />
                        </b-col>

                        <b-col class="actions-column">
                            <fa-icon fixed-width
                                     size="lg"
                                     title="Delete"
                                     :icon="['fal','trash-alt']"
                            />
                        </b-col>
                    </b-form-row>
                </template>

                <template v-slot:cell(actions)="row">
                    <b-form-row class="text-right">
                        <b-col class="actions-column">
                            <b-button
                                v-b-popover.hover.auto="'Download this item.'"
                                size="sm"
                                variant="outline-tetradic-3"
                                @click.stop="downloadFile(row.item)"
                            >
                                <fa-icon :icon="['fas', 'download']" />
                            </b-button>
                        </b-col>

                        <b-col v-if="!formDisabled" class="actions-column">
                            <confirmable-delete-action-button
                                title="Delete this upload."
                                :disabled="formDisabled"
                                :actionable-item="row.item"
                                :icon-config="{
                                    variant: 'outline-danger',
                                    classes: ['fa-fw', 'fa-lg'],
                                }"
                                :delete-words="{itemType: 'Upload', action: 'Delete'}"
                                @deleteConfirmed="deleteFile(row.item)"
                            />
                        </b-col>
                        <b-col v-else class="action-placeholder" />
                    </b-form-row>
                </template>
            </b-table>
            <b-col v-if="isApplicant">
                <b-button
                    size="sm"
                    :disabled="formDisabled"
                    data-toggle="tooltip"
                    data-placement="left"
                    variant="outline-tetradic-3"
                    title="Upload Files"
                    @click="showUploadModal"
                >
                    <fa-icon :icon="['fas', 'plus']" />
                    Add Upload
                </b-button>
            </b-col>
        </b-form-row>

        <!-- file upload modal -->
        <fieldset :disabled="formDisabled">
            <b-modal ref="fileUploadModalRef" title="Upload File">
                <p class="my-4">
                    Click to select a file, then click the upload button
                </p>
                <div class="d-block text-center">
                    <b-form-file
                        id="upload_file"
                        ref="fileUploadRef"
                        v-model="uploadingFile"
                        choose-label="Attachment"
                    />
                </div>
                <hr>
                <p>
                    <strong>File Selected:</strong>
                    {{ uploadingFile && uploadingFile.name }}
                </p>
                <div slot="modal-footer" class="w-100">
                    <b-btn size="sm"
                           variant="secondary"
                           class="mr-2"
                           @click="hideUploadModal"
                    >
                        <fa-icon fixed-width :icon="['fal', 'arrow-from-right']" />
                        Cancel
                    </b-btn>
                    <b-btn size="sm"
                           variant="success"
                           @click="uploadFile"
                    >
                        <fa-icon fixed-width :icon="['fas', 'upload']" />
                        Upload
                    </b-btn>
                </div>
            </b-modal>
        </fieldset>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
// import { Arc } from '$Shared/@arc/vue-arc';

// NOTE: This isn't a good example of a generic component
//   a better approach would be to pass in the columns, or at least the FICE specific
//   ones, as props and overwrite/merge as needed
export default {
    name: 'UploadedFilesList',

    props: {
        eeCode: { type: [String, Number], default: null },
        // TODO: uploadedFiles, isApplicant, columnDefinitions
    },

    data() {
        return {
            uploadingFile: null,
            filter: '',
        };
    },

    computed: {
        ...mapState('historicalInventoryUploads', {
            uploadedFiles: (state) => state.uploadedFiles,
        }),

        ...mapGetters({
            isApplicant: 'auth/isApplicant',
            applicantName: 'ref/ee/ficeName',
            applicantAcronym: 'ref/ee/ficeShortName'
        }),

        uploadedFilesTableFields() {
            let baseColumns = [
                { key: 'originalFileName', label: 'File Name', sortable: true },
                {
                    key: 'uploadedOn',
                    label: 'Upload On',
                    sortable: true,
                    formatter: (value) => {
                        const date = new Date(value);
                        return `${date.toLocaleDateString()} @ ${date.toLocaleTimeString()}`;
                    }
                },
                /*
                {
                    key: 'size',
                    label: 'Size',
                    sortable: true,
                    formatter: (value) => Arc.Numbers.bytesToHumanReadable(value),
                },
                */
                {
                    key: 'actions',
                    label: 'Actions',
                    sortable: false,
                    thStyle: { 'max-width': '7rem', width: '7rem' },
                },
            ];

            let reviewerColumns = [
                { key: 'refEeCode', label: 'FICE', sortable: true },
                {
                    key: 'applicantName',
                    label: 'Applicant',
                    sortable: true,
                    formatter: (applicantName) => `(${applicantName.short}) ${applicantName.long}`,
                },
                ...baseColumns
            ];

            return this.isApplicant
                ? baseColumns
                : reviewerColumns;
        },

        uploadedFilesTableItems() {
            // BUG?: "It is currently not possible to filter based on result of formatting via scoped field slots."
            //   https://bootstrap-vue.org/docs/components/table#filtering
            return this.isApplicant
                ? this.uploadedFiles
                : this.uploadedFiles.map((upload) => {
                    const eeCode = upload.refEeCode;

                    return {
                        ...upload,
                        applicantName: {
                            long: `${this.applicantName(eeCode)}`,
                            short: `${this.applicantAcronym(eeCode)}`
                        }
                    };
                });
        },

        formDisabled() {
            return !this.isApplicant;
        },
    },

    async mounted() {
        await this.$store.dispatch('historicalInventoryUploads/fetchUploads', this.eeCode);
    },

    methods: {
        // TODO: Split out upload modal to separate component and listen for events
        showUploadModal() {
            this.$refs.fileUploadModalRef.show();
        },

        hideUploadModal() {
            this.$refs.fileUploadRef.reset();
            this.uploadingFile = null;
            this.$refs.fileUploadModalRef.hide();
        },

        async fetchUploadedFiles() {
            await this.$store.dispatch('historicalInventoryUploads/fetchUploads', this.eeCode);
        },

        async uploadFile() {
            await this.$store.dispatch('historicalInventoryUploads/uploadFile', this.uploadingFile);

            this.fetchUploadedFiles();
            this.hideUploadModal();
        },

        deleteFile(fileInfo) {
            this.$emit('uploadedFile:delete', fileInfo);
            this.$store.dispatch(
                'historicalInventoryUploads/deleteFile',
                { uploadId: fileInfo.historicalInventoryUploadId }
            );
        },

        downloadFile(fileInfo) {
            this.$emit('uploadedFile:download', fileInfo);
            this.$store.dispatch(
                'historicalInventoryUploads/downloadFile',
                { ...fileInfo }
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import '$StyleVars';

$action-button-width: 32px;
$estimated-description-column-width: calc(
    83vw - (11ch + 8ch + 22ch + (#{$action-button-width * 3}) + (16px * 3))
);

.table {
    .actions-column .col {
        padding-left: 0;
        padding-right: 0;
        white-space: nowrap;

        .action-placeholder {
            width: $action-button-width;
        }
    }

    .money-column {
        text-align: right;
    }

    /deep/ thead {
        th.description {
            width: calc(100% - (50px + 50px + 80px + 125px));
        }

        th.actions-header,
        th.actions-header-1,
        th.actions-header-2,
        th.actions-header-3,
        th.actions-header-4 {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            white-space: nowrap;

            .col {
                padding: 5px 9px;

                svg {
                    vertical-align: bottom;
                }
            }
        }

        th.actions-header-1 {
            width: ($action-button-width + 8px) * 1;
            min-width: ($action-button-width + 8px) * 1;
        }

        th.actions-header-2 {
            width: ($action-button-width + 8px) * 2;
            min-width: ($action-button-width + 8px) * 2;
        }

        th.actions-header-3 {
            width: ($action-button-width + 8px) * 3;
            min-width: ($action-button-width + 8px) * 3;
        }

        th.actions-header-4 {
            width: ($action-button-width + 8px) * 4;
            min-width: ($action-button-width + 8px) * 4;
        }
    }

    /deep/ tbody {
        tr:hover {
            cursor: pointer;
        }

        td {
            /* white-space: nowrap; */

            .description {
                position: relative;
                width: $estimated-description-column-width;

                &:before {
                    content: attr(title);
                    max-width: 100%;
                    overflow: hidden;
                    position: absolute;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                &:after {
                    content: attr(title);
                    display: inline-block;
                    height: 0;
                    overflow: hidden;
                }
            }
        }
    }
}

</style>
